import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import MicIcon from '@mui/icons-material/Mic';
import MicNoneIcon from '@mui/icons-material/MicNone';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { colors } from '@/theme/colors';
import { ThemedTooltip } from '@/components/chat/FeedbackContainer';
import PulseIcon from '../../../assets/images/icons-outline/pulse.svg?react';
import VoiceWave from '../../../assets/images/icons-outline/voice-wave-outline.svg?react';
import { useChatStore } from '@/hooks/useChatStore';
import { useGlobalToast } from '@/providers/globalToastProvider';
import AutocompleteSuggestions from './AutoCompleteSuggestion';

type Props = {
  input: string;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  listening: boolean;
  startTranscription: () => void;
  stopTranscription: () => void;
  handleSendMessage: (message?: any, suggestion?: any) => void;
  handlePulseClick: () => void;
  isStreaming: boolean;
  showMaxCharTooltip: boolean;
  isImpersonating?: boolean;
};

const ChatInputContainer: React.FC<Props> = ({
  input,
  handleInputChange,
  handleKeyPress,
  listening,
  startTranscription,
  stopTranscription,
  handleSendMessage,
  handlePulseClick,
  isStreaming,
  showMaxCharTooltip,
  isImpersonating,
}) => {
  const textFieldRef = useRef<HTMLTextAreaElement>(null);
  const { t } = useTranslation(['chat']);
  const theme = useTheme();
  const { setVoiceMode } = useChatStore();
  const { showToast } = useGlobalToast();
  useEffect(() => {
    if (textFieldRef.current) {
      textFieldRef.current.focus();
      textFieldRef.current.scrollTop = textFieldRef.current.scrollHeight;
    }
  }, [input]);
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      const activeEl = document.activeElement;
      const isInputFocused =
        activeEl &&
        (activeEl.tagName === 'INPUT' ||
          activeEl.tagName === 'TEXTAREA' ||
          activeEl.getAttribute('contenteditable') === 'true');

      const isModifierPressed = e.ctrlKey || e.metaKey || e.altKey;

      // Don't hijack key events for shortcuts or when in another input
      if (!isInputFocused && !isModifierPressed) {
        textFieldRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  useEffect(() => {
    if (!isStreaming && textFieldRef.current) {
      textFieldRef.current.focus();
    }
  }, [isStreaming]);

  const checkMicrophonePermission = async (): Promise<boolean> => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        return false;
      }
      if (navigator.permissions && navigator.permissions.query) {
        const permission = await navigator.permissions.query({
          name: 'microphone' as PermissionName,
        });
        if (permission.state === 'denied') {
          return false;
        }
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (error) {
      console.error('Microphone permission check failed:', error);
      return false;
    }
  };

  const handleVoiceModeClick = async () => {
    try {
      const hasPermission = await checkMicrophonePermission();
      if (!hasPermission) {
        showToast({
          message: t('microphoneAccessRequired'),
          severity: 'warning',
        });
        return;
      }
      setVoiceMode(true);
      // console.log('✅ Voice Mode activated successfully');
    } catch (error) {
      console.error('❌ Error activating Voice Mode:', error);
      showToast({
        message: t('microphoneAccessRequired'),
        severity: 'error',
      });
    }
  };

  return (
    <Box
      data-testid="chat-input-container"
      width="100%"
      sx={{
        padding: '10px 15px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        background: theme.palette.background.default,
      }}
    >
      <div
        data-testid="chat-input-wrapper"
        style={{
          textAlign: 'center',
          position: 'relative',
        }}
      >
        <AutocompleteSuggestions
          inputValue={input}
          onSuggestionClick={handleSendMessage}
        />

        {showMaxCharTooltip && (
          <Typography
            variant="notesFooter"
            color="white"
            sx={{
              fontSize: '0.8rem',
              position: 'absolute',
              top: '-26px',
              left: '50%',
              alignSelf: 'center',
              transform: 'translateX(-50%)',
              backgroundColor: '#2C2C2C',
              padding: '6px 10px',
              borderRadius: '4px',
              whiteSpace: 'nowrap',
              boxShadow: '0px 2px 5px rgba(0,0,0,0.2)',
              opacity: 0,
              animation: 'fadeInOut 5s ease-in-out forwards',
              '@keyframes fadeInOut': {
                '0%': { opacity: 0 },
                '10%': { opacity: 1 },
                '90%': { opacity: 1 },
                '100%': { opacity: 0 },
              },
            }}
          >
            ⚠️ {t('maxInputLimitText')}
          </Typography>
        )}

        <TextField
          inputRef={textFieldRef}
          data-testid="chat-input"
          variant="outlined"
          placeholder={t('askAnything')}
          value={input}
          // disabled={isImpersonating}
          fullWidth
          multiline
          minRows={1}
          maxRows={4}
          onChange={handleInputChange}
          onKeyDown={handleKeyPress}
          sx={{
            maxWidth: '600px',
            mt: 2,
            borderRadius: '32px',
            '& .MuiOutlinedInput-root': {
              width: '614px',
              padding: {
                xs: '4px 8px',
                sm: '4px 16px',
              },
              minHeight: '58px',
              borderRadius: '50px',
              alignItems: 'center',
              fontFamily: 'Roboto',
              fontWeight: 'regular',
              fontSize: '1em',
              lineHeight: '18px',
              textAlign: 'left',
              textUnderlinePosition: 'from-font',
              textDecorationSkipInk: 'none',
              [theme.breakpoints.down('sm')]: {
                width: '355px',
                minHeight: '44px',
                margin: '0 auto',
              },
            },
            '& .MuiOutlinedInput-input::placeholder': {
              color: colors.placeHolder,
              opacity: '1',
            },
          }}
          InputProps={{
            startAdornment: (
              <>
                <IconButton
                  data-testid="pulse-button"
                  onClick={handlePulseClick}
                  sx={{
                    color: 'white',
                    height: '2rem',
                    width: '2rem',
                    '&:hover': {
                      backgroundColor:
                        theme.palette.mode === 'dark' ? '' : colors.mistGray,
                    },
                  }}
                >
                  <Box
                    sx={{
                      height: {
                        xs: '1.5rem',
                        sm: '1.85rem',
                      },
                      width: {
                        xs: '1.5rem',
                        sm: '1.85rem',
                      },
                      justifyContent: 'center',
                      alignItems: 'center',
                      display: 'flex',
                    }}
                  >
                    <PulseIcon
                      fill={theme.palette.primary.main}
                      color={theme.palette.primary.main}
                    />
                  </Box>
                </IconButton>
                {/* <InputAdornment
                  position="start"
                  sx={{
                    width: '24px',
                    height: '24px',
                    top: '8px',
                    left: '12.58px',
                  }}
                >
                  <AddIcon
                    sx={{
                      color: '#58595B',
                      top: '5px',
                      left: '5px',
                    }}
                  />
                </InputAdornment> */}
              </>
            ),
            endAdornment: (
              <>
                {/* Microphone Animation While Listening */}
                {
                  // !isImpersonating &&
                  listening ? (
                    <InputAdornment position="end">
                      <Box
                        data-testid="mic-button-wrapper"
                        sx={{
                          position: 'relative',
                          display: 'inline-flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          '&::before, &::after': {
                            content: '""',
                            position: 'absolute',
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            backgroundColor: '#D3DAE9',
                            animation:
                              'pulse 1.2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                          },
                          '&::after': {
                            animationDelay: '0.3s',
                          },
                          '@keyframes pulse': {
                            '0%': {
                              transform: 'scale(1.1)',
                              opacity: 1,
                            },
                            '85%': {
                              transform: 'scale(2.5)',
                              opacity: 0,
                            },
                            '85.01%, 100%': {
                              transform: 'scale(1.1)',
                              opacity: 0,
                            },
                          },
                        }}
                      >
                        <ThemedTooltip
                          title={t('stopListening')}
                          placement="top"
                        >
                          <IconButton
                            data-testid="stop-mic-button"
                            onClick={stopTranscription}
                            sx={{
                              position: 'relative',
                              bgcolor: '#19469D',
                              zIndex: 1,
                              color: 'white',
                              height: {
                                xs: '1.9rem',
                                sm: '2rem',
                              },
                              width: {
                                xs: '1.9rem',
                                sm: '2rem',
                              },
                              animation:
                                'buttonPulse 1.2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                              '@keyframes buttonPulse': {
                                '0%': { transform: 'scale(1)' },
                                '15%': { transform: 'scale(1.1)' },
                                '30%': { transform: 'scale(1)' },
                                '100%': { transform: 'scale(1)' },
                              },
                              '&:hover': {
                                bgcolor: '#143578',
                              },
                            }}
                          >
                            <MicIcon
                              sx={{
                                height: {
                                  xs: '1.5rem',
                                  sm: '1.75rem',
                                },
                                width: {
                                  xs: '1.5rem',
                                  sm: '1.75rem',
                                },
                              }}
                            />
                          </IconButton>
                        </ThemedTooltip>
                      </Box>
                    </InputAdornment>
                  ) : input.trim() !== '' ? (
                    <InputAdornment position="end">
                      <ThemedTooltip title={t('submit')} placement="top">
                        <IconButton
                          data-testid="send-button"
                          sx={{
                            backgroundColor: colors.darkTextTertiary,
                            height: '2rem',
                            width: '2rem',
                            '&:hover': {
                              backgroundColor:
                                theme.palette.mode === 'dark'
                                  ? '#ffffff'
                                  : colors.blue100,
                            },
                          }}
                          onClick={handleSendMessage}
                          aria-label="send"
                          disabled={isStreaming}
                        >
                          <ArrowUpwardIcon
                            sx={{
                              color: colors.black,
                              height: '1.5rem',
                              width: '1.5rem',
                            }}
                          />
                        </IconButton>
                      </ThemedTooltip>
                    </InputAdornment>
                  ) : (
                    <InputAdornment position="end">
                      <ThemedTooltip title={t('useMicrophone')} placement="top">
                        <IconButton
                          data-testid="mic-button"
                          onClick={startTranscription}
                          aria-label="microphone"
                          sx={{
                            height: '2rem',
                            width: '2rem',
                            '&:hover': {
                              backgroundColor:
                                theme.palette.mode === 'dark'
                                  ? ''
                                  : colors.mistGray,
                            },
                          }}
                        >
                          <MicNoneIcon
                            sx={{
                              color: theme.palette.primary.main,
                              height: {
                                width: '1.5rem',
                                xs: '1.25rem',
                                sm: '1.5rem',
                              },
                              width: {
                                xs: '1.25rem',
                                sm: '1.5rem',
                              },
                            }}
                          />
                        </IconButton>
                      </ThemedTooltip>
                      <ThemedTooltip title={t('useVoiceMode')} placement="top">
                        <IconButton
                          data-testid="voice-button"
                          onClick={handleVoiceModeClick}
                          sx={{
                            color: 'white',
                            height: '2rem',
                            width: '2rem',
                            '&:hover': {
                              backgroundColor:
                                theme.palette.mode === 'dark'
                                  ? ''
                                  : colors.mistGray,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              height: {
                                xs: '1.5rem',
                                sm: '1.85rem',
                              },
                              width: {
                                xs: '1.5rem',
                                sm: '1.85rem',
                              },
                              justifyContent: 'center',
                              alignItems: 'center',
                              display: 'flex',
                            }}
                          >
                            <VoiceWave
                              fill={theme.palette.primary.main}
                              color={theme.palette.primary.main}
                            />
                          </Box>
                        </IconButton>
                      </ThemedTooltip>
                    </InputAdornment>
                  )
                }
              </>
            ),
          }}
        />
      </div>
      <Typography
        data-testid="chat-disclaimer"
        variant="body2"
        color={colors.royal80}
        sx={{
          mt: 2,
          fontSize: '12px',
          fontFamily: 'Roboto',
          fontWeight: 'regular',
          maxWidth: '695px',
        }}
      >
        {t('disclaimer')}
      </Typography>
    </Box>
  );
};

export default ChatInputContainer;
